<script setup lang="ts">
import { Heading, Text } from '~ds/components/ui/typography';
import KUIUnifiedBadgeProgress from '~kui/components/KUI/Unified/Badge/KUIUnifiedBadgeProgress.vue';
import KUIUnifiedSpotlight from '~kui/components/KUI/Unified/Spotlight/KUIUnifiedSpotlight.vue';
import { useConfetti } from '~kui/composables/useConfetti';
import { useMotionPresets } from '~kui/composables/useMotionPresets';
import { motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

import type { CompletionSpotlight, CompletionSpotlightLinkClick } from './types';

export interface CompletionActivity {
  id: string;
  completed: boolean;
}

interface Props {
  heading: string;
  badgeUrl: string;
  partnerBgColor: string;
  activities: CompletionActivity[];
  premiumBadgeUrl?: string | null;
  iconPath?: string;
  bonusCoins?: number | null;
  animate?: boolean;
  spotlights?: CompletionSpotlight[];
  partialUnlockMessage?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  premiumBadgeUrl: null,
  iconPath: '/images/kui/unified/anyway-shield-badge.webp',
  bonusCoins: null,
  animate: false,
  spotlights: () => [],
  partialUnlockMessage: null,
});

const emit = defineEmits<{
  spotlightLinkClick: [click: CompletionSpotlightLinkClick];
}>();

const { t } = useI18n();
const { SLIDE_UP, TRANSITION } = useMotionPresets();
const { triggerConfetti } = useConfetti();

const isFullyCompleted = computed(() =>
  props.activities.length > 0 && props.activities.every(a => a.completed),
);

// Gate the 3D badge on the final completion state (not the animated
// displayActivities) so the model mounts and loads from the start — swapping
// it in mid-reveal would blank the badge while the model streams in. A
// partially-completed collection keeps the grayscale static badge.
const premiumSrc = computed(() =>
  (isFullyCompleted.value ? props.premiumBadgeUrl : null),
);

const hasBonusCoins = computed(() =>
  isFullyCompleted.value && props.bonusCoins && props.bonusCoins > 0,
);

// Whether a spotlight has been earned is the host's call: a collection's only arrives once the
// collection is finished, an activity's arrives with the activity itself.
const activeSpotlights = computed(() =>
  props.spotlights.filter(spotlight => spotlight.links.length > 0),
);

const completedCount = computed(() => props.activities.filter(a => a.completed).length);
const previouslyCompleted = computed(() => Math.max(completedCount.value - 1, 0));
const revealedCount = ref(props.animate ? previouslyCompleted.value : completedCount.value);
const phase = ref<'reveal' | 'unlock' | 'final'>(props.animate ? 'reveal' : 'final');
const displayedBonusCoins = ref(props.animate ? 0 : (props.bonusCoins ?? 0));

const displayActivities = computed(() =>
  props.activities.map((a, i) => ({
    ...a,
    completed: i < revealedCount.value,
  })),
);

const timers: ReturnType<typeof setTimeout>[] = [];
let rafId: number | null = null;

const countUp = (target: number, duration: number) => {
  const start = performance.now();
  const step = (now: number) => {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - (1 - progress) ** 3;
    displayedBonusCoins.value = Math.round(eased * target);
    if (progress < 1) {
      rafId = requestAnimationFrame(step);
    }
  };
  rafId = requestAnimationFrame(step);
};

onMounted(() => {
  if (!props.animate) {
    return;
  }

  const DELAY_BEFORE_START = 600;

  timers.push(setTimeout(() => {
    revealedCount.value = completedCount.value;
  }, DELAY_BEFORE_START));

  const BADGE_ANIMATION_DURATION = 600;
  const afterRevealDelay = DELAY_BEFORE_START + BADGE_ANIMATION_DURATION;

  if (isFullyCompleted.value) {
    timers.push(setTimeout(() => {
      phase.value = 'unlock';
      triggerConfetti({ particleCount: 120, spread: 80, origin: { y: 0.5 } });
    }, afterRevealDelay));

    timers.push(setTimeout(() => {
      phase.value = 'final';
      if (hasBonusCoins.value) {
        countUp(props.bonusCoins!, 800);
      }
    }, afterRevealDelay + 1000));
  }
  else {
    timers.push(setTimeout(() => {
      phase.value = 'final';
    }, afterRevealDelay));
  }
});

// The bonus coins own the slot first and hold it for their own moment; the spotlight only
// takes it once that beat is over. With no coins to show there is no beat to wait out.
const SPOTLIGHT_REVEAL_DELAY_MS = 2000;
const showSpotlight = ref(false);

watch(
  () => activeSpotlights.value.length > 0 && phase.value === 'final',
  (readyForSpotlight) => {
    if (!readyForSpotlight || showSpotlight.value) {
      return;
    }
    if (!hasBonusCoins.value) {
      showSpotlight.value = true;
      return;
    }
    timers.push(setTimeout(() => {
      showSpotlight.value = true;
    }, SPOTLIGHT_REVEAL_DELAY_MS));
  },
  { immediate: true },
);

const coinsSlotClass = computed(() => {
  if (showSpotlight.value) {
    return 'pointer-events-none -translate-y-6 opacity-0';
  }
  return phase.value === 'final'
    ? 'translate-y-0 scale-100 opacity-100'
    : 'pointer-events-none translate-y-6 scale-90 opacity-0';
});

onUnmounted(() => {
  timers.forEach(clearTimeout);
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
  }
});
</script>

<template>
  <div class="flex flex-col items-center gap-6 sm:gap-10 ">
    <motion.div
      class="text-center mb-4"
      v-bind="SLIDE_UP"
      :transition="TRANSITION"
    >
      <Heading
        size="md"
        as="h2"
        class="max-w-xs leading-tight transition-opacity duration-500 sm:text-2xl"
        :class="phase === 'unlock' && isFullyCompleted ? 'opacity-0' : 'opacity-100'"
      >
        {{ heading }}
      </Heading>
    </motion.div>

    <motion.div
      class="flex flex-col items-center gap-4 transition-transform duration-700 ease-out"
      :class="phase !== 'reveal' && isFullyCompleted ? 'scale-130' : 'scale-100'"
      v-bind="SLIDE_UP"
      :transition="{ ...TRANSITION, delay: 0.1 }"
    >
      <KUIUnifiedBadgeProgress
        :image-url="badgeUrl"
        :image-alt="t('unified.activity.badge.displayedSkill')"
        badge-size="size-60 md:size-72"
        :progress="displayActivities"
        :premium-badge-url="premiumSrc"
        :icon-path="iconPath"
        :hide-progress-row="phase !== 'reveal' && isFullyCompleted"
        :partner-bg-color="partnerBgColor"
      />
    </motion.div>

    <div
      v-if="!isFullyCompleted"
      class="flex flex-col items-center gap-1 text-center transition-all duration-600 ease-out"
      :class="phase === 'final' ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'"
    >
      <Text size="xl" class="font-medium">
        {{ t('unified.activity.completion.partialCount', { completed: completedCount, total: activities.length }) }}
      </Text>
      <Text size="sm" class="max-w-44">
        {{ partialUnlockMessage ?? t('unified.activity.completion.partialUnlockMessage', { total: activities.length }) }}
      </Text>
    </div>

    <!-- One slot, two moments: the coins hold it, then hand it to the spotlight. -->
    <div
      v-if="hasBonusCoins || activeSpotlights.length"
      class="mt-4"
      :class="activeSpotlights.length ? 'grid w-full max-w-sm justify-items-center' : ''"
    >
      <div
        v-if="hasBonusCoins && phase !== 'reveal'"
        class="col-start-1 row-start-1 flex flex-col items-center gap-0 transition-all duration-600 ease-out"
        :class="coinsSlotClass"
        :aria-hidden="showSpotlight"
        data-testid="completion-bonus-coins"
      >
        <div class="rounded-full bg-secondary px-3 md:px-5 py-1.5 shadow-sm z-20">
          <Text size="lg" class="font-semibold text-foreground">
            {{ displayedBonusCoins.toLocaleString() }} {{ t('unified.activity.completion.bonusCoins') }}
          </Text>
        </div>

        <div
          class="bonus-coin-circle flex size-24 md:size-38 items-center justify-center rounded-full"
          :class="{ 'bonus-coin-float': hasBonusCoins && phase === 'final' && !showSpotlight }"
        >
          <NuxtImg
            src="/images/kui/unified/gold-coins@2x.webp"
            :alt="t('unified.activity.completion.bonusCoins')"
            class="size-22 md:size-28 drop-shadow-lg"
          />
        </div>
      </div>

      <!-- z-40 clears the completion screen's tap overlay (z-30) so a link tap reaches the link
           and never the step machine. `inert` keeps the links out of reach — of the keyboard and
           of a screen reader — for as long as the coins still hold the slot. The card sits over
           the busy completion background, so it carries a shadow the white placements don't need. -->
      <div
        v-if="activeSpotlights.length"
        :inert="!showSpotlight"
        data-testid="completion-spotlights"
        class="relative z-40 col-start-1 row-start-1 flex w-full flex-col gap-3 self-center transition-all duration-600 ease-out"
        :class="showSpotlight ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'"
      >
        <KUIUnifiedSpotlight
          v-for="spotlight in activeSpotlights"
          :key="spotlight.key"
          :heading="spotlight.heading"
          :image-url="spotlight.imageUrl"
          :image-alt="spotlight.imageAlt"
          :links="spotlight.links"
          class="w-full text-start shadow-lg"
          data-testid="completion-spotlight"
          @link-click="emit('spotlightLinkClick', { key: spotlight.key, link: $event })"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.bonus-coin-circle {
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
}

.bonus-coin-float {
  animation: coin-float 3s ease-in-out 0.6s infinite;
}

@keyframes coin-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
</style>
